// frontend/src/components/common/ListToolbar.jsx
import { useEffect, useRef, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import SearchTrigger from './SearchTrigger';
import FilterSheet from './FilterSheet';
import ResultCount from './ResultCount';
import RowActionsMenu from './RowActionsMenu';
import ActiveFilterBar from './ActiveFilterBar';
import { useTableWidth } from './tableWidth';
import { FIELD_HEIGHT_SX, SP, SUCHE_MIN_BREITE_KOMPAKT } from '../../theme/tokens';

// 🔴 Der Kurzbefehl der Listensuche ist EINZELINSTANZ (G0.3, `SearchTrigger`
// @props `shortcut`): sein Zuhörer sitzt auf `document`, zwei Suchfelder mit
// demselben Zeichen nähmen also BEIDE den Fokus. Die Leisten zweier Listen auf
// einer Seite wissen nichts voneinander — deshalb trägt ihn immer GENAU EINE,
// und zwar die erste in dieser modulweiten Warteliste. Jede weitere meldet die
// Belegung einmal und setzt ihn NICHT. Der erste Weg („nur eine trägt ihn")
// wurde dem Wurf vorgezogen: ein Wurf risse eine Seite mit zwei Listen auf,
// obwohl genau eine Suche den Kurzbefehl tragen kann und die andere einfach
// keinen braucht.
//
// 🔴 R16-W6 (2026-10-12): eine blosse Marke genügte nicht. Wird die tragende
// Leiste ABGEBAUT, während eine zweite noch steht, hat die zweite ihren Effekt
// längst ausgeführt und kehrt ohne Registrierung zurück — danach trüge KEINE
// den Kurzbefehl, und weil `shortcut` sich nicht ändert, liefe der Effekt auch
// nicht erneut. Die Warteliste gibt ihn beim Abbau an die nächste weiter.
const kurzbefehlWartende = [];

/** Verteilt den Kurzbefehl neu: genau der ERSTE Wartende trägt ihn. */
function verteileKurzbefehl() {
  kurzbefehlWartende.forEach((melden, i) => melden(i === 0));
}

/**
 * ListToolbar — the data-panel toolbar (Z1, Z2, Z3) of a list view (BS2, G0.10).
 *
 * @description
 *   Orders the three zones of the list anatomy in the FIXED order of
 *   `tabellen-filter-suche.md` §2.1 and switches the mobile form (§11.3). It
 *   composes the G0 building blocks — it invents none:
 *
 *   - **Z1 (`liste-z1`)** — search (`SearchTrigger`, BS5) · facets (≤ 3, desktop
 *     only, `BottomSheetSelect`/`…MultiSelect` `desktopForm="pill"`, BS7) · the
 *     „Filters" button (`FilterSheet variant="popover" countInLabel`, BS6) ·
 *     the result counter (`ResultCount`, BS4; **desktop only**) · refresh · the
 *     page actions (⋮, `RowActionsMenu`, BS18)
 *   - **Z2 (`liste-z2`)** — the quick-filter pills (`FilterPillRow`, BS8)
 *   - **Z3 (`liste-z3`)** — the active-filter row (`ActiveFilterBar`, BS3),
 *     fed by `query.chips`/`query.clearAll`
 *
 *   🔴 **The zone order is Z1 → Z2 → Z3 → table** (§2.1, probe W.1): a filter
 *   that sits above the search, or a tab below the filters, is the defect P3
 *   measures. `children` (the table + footer) render AFTER Z3, so a caller can
 *   compose the whole panel and the order is checkable in ONE tree.
 *
 *   🔴 **One height per row** (§2.1 N2.4, probe W.2): every DIRECT child of
 *   `liste-z1` is a slot that carries `FIELD_HEIGHT_SX`, and the inner control
 *   follows — the search field via `& .MuiInputBase-root` (the documented house
 *   form, `tokens.js` `FIELD_HEIGHT_SX`), the „Filters" button via
 *   `& .MuiButton-root`. The facet pills already carry `FACET_PILL_SX`, which is
 *   `FIELD_HEIGHT_SX` plus the press gate (`tokens.js` `FACET_PILL_SX`). Without
 *   the wrapper the button would stand at MUI's own small height and the row
 *   would carry two heights — the very defect (T3-5) that `FIELD_HEIGHT_SX`
 *   exists to end.
 *
 *   ⚠️ **What W.2 proves, and what it does NOT (R15-W8):** the probe compares the
 *   height TOKEN of the direct Z1 children in the EMITTED CSS. It does NOT measure
 *   a pixel height and does NOT prove that the INNER control really follows — jsdom
 *   has no layout (Regel 17 §3b). The browser measurement belongs to the
 *   Musterroute (G0.11, P4/P6). The rule is stated here so that it is testable at
 *   all; „measured" must not be read into this paragraph.
 *
 *   🔴 **Mobile form (§11.3/N11.8):** Z1 = the search pill + „Filters", both
 *   FIXED (not in a scroll container — the „Filters" button in the pill row is
 *   the defect T3-12). Facets are NOT inline on the phone (they live in the
 *   sheet, `moreFilters.criteria`), and the counter moves to Z3 (§11.3/N11.10) —
 *   there is still exactly ONE counter per list (P8), only its zone changes.
 *
 *   🔴 **`kompakt` (R15/Fotobefund, Nachbesserung 3, 2026-10-18):** derselbe
 *   Z1-Schnitt (Suche + „Filters", Facetten im Blatt, Zähler in Z3), aber vom
 *   AUFRUFER verlangt statt von der Fensterbreite. Grund: eine Ansicht, die
 *   schon unter `md` auf die Karte wechselt (`/overview`), zeigt bei 768 px die
 *   Kartenhälfte — die Desktop-Z1 mit Facette und Zähler drückte das Suchfeld
 *   dort auf ~0 px. `kompakt` ist rein additiv (Vorgabe `false`); die
 *   Fensterbreite bleibt die zweite, unabhängige Bedingung, und unter `sm` ist
 *   die Form ohnehin schon die mobile.
 *
 *   🔴 **The result counter is ONE** (P8): desktop → Z1, mobile → Z3. This
 *   component renders it in exactly one of the two, never both.
 *
 *   🔴 **Der Kurzbefehl ist Einzelinstanz UND wandert weiter** (G0.3/R16-W6): der
 *   Zuhörer von `SearchTrigger.shortcut` sitzt auf `document`, zwei Listensuchen
 *   mit demselben Zeichen nähmen beide den Fokus. Die Leisten stehen deshalb in
 *   einer modulweiten **Warteliste** — die erste trägt ihn, jede weitere meldet
 *   die Belegung einmal (nur außerhalb `production`) und setzt ihn NICHT. Wird
 *   die tragende Leiste abgebaut, während eine zweite noch steht, übernimmt die
 *   zweite; eine bloße Marke ließe danach **keine** übrig, weil die zweite ihren
 *   Effekt längst ausgeführt hat und `shortcut` sich nicht ändert.
 *
 *   🔴 **Fokus-Rückgabe aus Z3** (§4.3): `ActiveFilterBar` bekommt hier
 *   `returnFocusRef` auf das Suchfeld gereicht — nach dem Entfernen des letzten
 *   Chips bzw. nach „Clear all" geht der Fokus dorthin. Die Senke ist dieselbe,
 *   die `SearchTrigger` für sein eigenes ✕ benutzt (`SearchTrigger.focusRef`);
 *   ein zweiter Weg wird nicht erfunden.
 *
 *   🔴 **Pflichtnamen werfen, statt still zurückzufallen** (house form
 *   `EmptyState.reason`): `actionsLabel` is REQUIRED as soon as `actions` has
 *   entries (the ⋮ has no visible text) and MORE THAN THREE facets throw (a
 *   fourth facet is a toolbar, not a facet row — Polaris `promotedBulkActions`
 *   reasoning, `BulkActionBar` throws on > 3 actions the same way).
 *
 *   🔴 **No number, no colour literal** (Regel 22): `FIELD_HEIGHT_SX`, `SP`. The
 *   panel surface around it belongs to the caller (the Musterroute G0.11).
 *
 * @useWhen
 *   - The toolbar of a list view (table or card list) inside the data panel
 *   - A view whose query lives in `useListQuery` (BS1) — `query.chips` feeds Z3
 *   - The mobile form of the same list: search pill + „Filters" with the count
 *
 * @dontUseFor
 *   - The selection bar of a list — that is `BulkActionBar` (BS16); it replaces
 *     the TABLE HEADER, not this toolbar, and shows no query state
 *   - A page header (breadcrumb, title, primary action) — that is `ViewHeader`
 *   - A filter panel without a list — compose `FilterSheet` directly
 *
 * @related
 *   - `SearchTrigger` (BS5) — Z1 search; `shortcut` is claimed here (single instance)
 *   - `FilterSheet` (BS6) — the „Filters" button; `variant="popover"` on desktop
 *   - `FilterPillRow` (BS8) — Z2, handed over as `quickFilters`
 *   - `ActiveFilterBar` (BS3) — Z3, rendered from `query`
 *   - `ResultCount` (BS4) — the one counter (Z1 desktop / Z3 mobile)
 *   - `useListQuery` (BS1) — the `query` contract (`chips`, `clearAll`)
 *
 * @reuseHints
 *   - Every list migration (G1–G15) drops its hand-rolled `Stack` toolbar onto this
 *   - The two-row toolbar of `/rma` and the four hand-rolled chip rows are the first callers
 *
 * @props
 *   search        — SearchTrigger config `{ value, onChange, onCommit?, placeholder,
 *                   label?, loading?, disabled?, inputRole? }`. 🔴 It is a CONFIG, not a node: this
 *                   component owns the element, because `shortcut` is
 *                   single-instance and only a component that RENDERS the field
 *                   can decide who gets it.
 *                   🔴 **`search.disabled` (G0.12, N8.5, rein additiv):** die Leiste
 *                   reicht es über das BESTEHENDE `{...search}` an `SearchTrigger`
 *                   durch — **kein eigenes Feld**. Ohne den Schlüssel ist der Baum
 *                   der Leiste unverändert.
 *                   🔴 **`search.inputRole` (G0.12, P5, rein additiv):** dieselbe
 *                   Durchreichung — die Rolle des Eingabefelds (Hauswert
 *                   `'searchbox'`, Konzept N7.1/N7.5/P5/P34). Auch hier **kein
 *                   eigenes Feld**; ohne den Schlüssel bleibt das Feld `textbox`.
 *                   🔴 **Der Deckel des Kastens kommt NICHT aus dieser Config,
 *                   sondern aus dem Melde-Kanal** (`tableWidth.jsx`, G0b.4):
 *                   meldet die Tabelle der Seite `SEARCH_MAX`, deckelt der Kasten
 *                   auf diesen Wert (NW4.2:648); ohne Meldung füllt er den Rest
 *                   von Z1 (N2.9 wörtlich). Die 40 % aus N2.9 sind die
 *                   `reading`/`wide`-Hälfte derselben Regel.
 *   facets        — ReactNode[] — the facet pills for Z1 (desktop only). ≤ 3;
 *                   MORE throw. Put the same criteria in `moreFilters.criteria`
 *                   for the phone (they are never inline under `sm`, N11.8).
 *   moreFilters   — FilterSheet config `{ criteria, sort, resultCount, activeCount,
 *                   onClearAll?, label?, title?, countInLabel? }`. Rendered as
 *                   `<FilterSheet variant="popover" countInLabel …/>`; the caller
 *                   may override either.
 *                   🔴 **`activeCount` zählt die BLATT-KRITERIEN MIT WERT
 *                   einschließlich Vorgaben (N4.6) und ist NICHT `query.chips.length`.**
 *                   Der Such-Chip „Search: \<Begriff\>" (N4.1 `:383`, N7.10 `:518`)
 *                   steht in `query.chips`, wenn der Aufrufer die Suche als
 *                   Dimension deklariert (`kind: 'single'` ohne `values`,
 *                   `frontend/src/components/common/listQuery/useListQuery.js:96`;
 *                   `baueChips` `frontend/src/components/common/listQuery/useListQuery.js:680`).
 *                   Einen zweiten Kanal gibt es nicht. Diese Leiste leitet `activeCount` bewusst nicht
 *                   daraus ab — das wäre eine neue Doppelquelle statt einer
 *                   weniger (R16-W4).
 *   kompakt       — `false` (default) | `true`: force the COMPACT Z1 form
 *                   (search + „Filters", no inline facets, counter in Z3) at
 *                   any width. The caller sets it when its own card/table
 *                   switch already sits at `md` and the desktop Z1 would
 *                   squeeze the search to nothing (R15/Fotobefund 768 px,
 *                   `/overview`). 🔴 It does NOT change the search control
 *                   itself — `SearchTrigger` keeps its own breakpoint; only
 *                   the Z1 composition moves.
 *   quickFilters  — node — Z2 (a `FilterPillRow`). Omitted, Z2 does not render.
 *   count         — ResultCount config `{ n, m, noun, unknownTotal, unknown, loading }`
 *   view          — node — „Ansicht/Sortierung" in Z1 (§2.1 5a), ZWISCHEN dem
 *                   Trefferzähler und dem Auffrischen. 🔴 **Nur ohne Tabellenkopf**
 *                   (§2.1 5a: „Ansicht/Sortierung (nur ohne Tabellenkopf)", §9.2):
 *                   trägt die Liste einen Tabellenkopf, sitzt die Sortierung DORT
 *                   — ein zweiter Sortierweg in der Leiste wäre die Doppelquelle,
 *                   gegen die die Zone angetreten ist. Die Leiste prüft das nicht
 *                   (sie sieht den Kopf nicht) — es ist eine Pflicht des Aufrufers.
 *                   🔴 **`view` rendert auf JEDER Breite in Z1** — anders als der
 *                   Zähler daneben steht es NICHT unter `!mobil`. 🔴 **Mobil wandert
 *                   die Sortierung in den ersten Abschnitt des Filter-Blatts**
 *                   (N11.14 `:788`, §11.4 `:775`, MF1 `:103`; gebaut als
 *                   `FilterSheet.sort`): `view` trägt sie nur ohne Tabellenkopf,
 *                   und der Aufrufer reicht sie nur im Desktopzweig durch. Die
 *                   **Ansicht**-Hälfte entscheidet §11.1 per Regel (R16-W8).
 *                   🟢 **Entschieden am 2026-10-12 durch den ersten Aufrufer, die
 *                   Musterroute** (`design/muster/ListenMuster.jsx`, G0.11): er
 *                   übergibt den Knoten auf beiden Breiten, führt das
 *                   **Rahmen-Segment** darin aber nur `!mobil` — mobil trägt Z1
 *                   nach N11.8 allein Such-Pille und „Filters". Der Knoten selbst
 *                   bleibt auch mobil stehen, weil die Musterroute dort ihre
 *                   Messflächen führt (P34 braucht ein zweites Suchfeld IN Z1).
 *                   Wer `view` ohne eigene Mobilform übergibt, reicht es
 *                   weiterhin nur im Desktopzweig durch.
 *   refresh       — node — the ONE refresh path (§2.1 N2.6), at the end of Z1
 *   actions       — RowActionsMenu entries (`{key,label,icon?,onClick,divider?,danger?,disabled?}`)
 *   actionsLabel  — REQUIRED with `actions`: the accessible name of the ⋮ (throws)
 *   query         — `{ chips, clearAll }` from `useListQuery` (BS1) — feeds Z3
 *   z3UmbruchMobil — `false` (default) | `true`: on the phone Z3 may wrap to a
 *                   second line instead of clipping a wide chip. Passed through
 *                   as `ActiveFilterBar.mobilUmbruch`. 🔴 Opt-in, because the
 *                   wrap costs a line on EVERY mobile list (measured: P2/M1 of
 *                   the `g11cc` chain).
 *   z1Extra       — node — OPT-IN (Vorgabe `undefined`), S3 2026-11-01: ein
 *                   BEDIENELEMENT der Liste, das in Z1 zwischen „Filters" und
 *                   dem Zähler steht — auf jeder Breite, auf der der AUFRUFER
 *                   es übergibt. Der Baustein entscheidet nichts über Breiten;
 *                   ob der Knoten unter `sm` in Z1 steht, legt die Liste fest.
 *                   Der Anlass ist der Testbelege-Schalter von `/invoicing`:
 *                   er ist kein Filter des Blattes (er gehört sichtbar in die
 *                   Leiste, L3) und keine Facette (die blendet unter `sm` aus,
 *                   N11.8). 🔴 Der Aufrufer stellt ihn unter `sm` NICHT in Z1,
 *                   sondern als erste Zeile in Z2 (`InvoiceList.jsx:1503`,
 *                   `:1508`) — in Z1 verdrängte er bei 390 px die Suchpille.
 *                   Der Facetten-Slot taugt deshalb nicht; `view` ist laut
 *                   Vertrag die Sortierung und nur ohne Tabellenkopf erlaubt.
 *                   🔴 Der Knoten wird in einen Kasten mit `FIELD_HEIGHT_SX`
 *                   gelegt, damit die Höhenzusage von W.2 (alle direkten Kinder
 *                   von `liste-z1` tragen dieselbe Höhe) auch mit ihm gilt.
 *                   🔴 Rein additiv: ohne die Prop entsteht KEIN Knoten und der
 *                   Baum ist zeichengleich zu vorher.
 *   shortcut      — the single-key shortcut of the list search (house value `'/'`).
 *                   🔴 Single instance: only the FIRST mounted toolbar claims it;
 *                   is that one unmounted, the next waiting one takes it over (R16-W6)
 *   children      — the table + footer; rendered after Z3 (the zone order)
 *   sx            — last applied, the caller wins
 */
export default function ListToolbar({
  search,
  facets,
  moreFilters,
  quickFilters,
  count,
  view,
  refresh,
  kompakt = false,
  actions,
  actionsLabel,
  query,
  z3UmbruchMobil = false,
  z1Extra,
  shortcut,
  children,
  sx = {},
}) {
  const theme = useTheme();
  const schmal = useMediaQuery(theme.breakpoints.down('sm'));
  // 🔴 Zwei Begriffe, absichtlich getrennt: `schmal` ist die FENSTERBREITE (und
  // steuert die Suchform, weil `SearchTrigger` seine eigene Abfrage hat);
  // `z1Kompakt` ist die FORM von Z1 — sie gilt zusätzlich, wenn der Aufrufer
  // `kompakt` setzt (R15/Fotobefund 768 px).
  const z1Kompakt = schmal || kompakt;
  // 🔴 G0b.4 (NW4.2:648) — der Deckel des Suchfelds kommt aus dem Melde-Kanal
  // (`tableWidth.jsx`): die Tabelle einer `full`-Seite meldet `SEARCH_MAX`, sonst
  // `null`. OHNE Meldung bleibt `null` und der Kasten fuellt wie bisher den Rest
  // von Z1 (N2.9 woertlich) — dieselbe additive Auflage wie ueberall hier.
  const { searchMax } = useTableWidth();
  // Die Fokus-Senke der Z3-Zeile (§4.3): `ActiveFilterBar` gibt den Fokus hierher
  // zurück, wenn kein Chip mehr steht bzw. nach „Clear all". Sie zeigt auf das
  // Eingabefeld der Suche — dieselbe Senke, die `SearchTrigger` selbst benutzt.
  const suchFeldRef = useRef(null);

  const facetten = (Array.isArray(facets) ? facets : (facets ? [facets] : [])).filter(Boolean);
  if (facetten.length > 3) {
    throw new Error(
      `ListToolbar: ${facetten.length} Facetten — erlaubt sind höchstens 3 (§2.1). ` +
        'Der Rest gehört ins Blatt (`moreFilters.criteria`).',
    );
  }

  const hatAktionen = Array.isArray(actions) && actions.length > 0;
  if (hatAktionen && !actionsLabel) {
    throw new Error(
      'ListToolbar: `actionsLabel` fehlt — der zugängliche Name des ⋮-Knopfes. ' +
        'Pflicht, sobald `actions` Einträge hat (Hausform `BulkActionBar.moreLabel`).',
    );
  }

  // 🔴 Einzelinstanz des Kurzbefehls (s. o. am Modul). Der Effekt trägt sich in
  // die Warteliste ein und trägt ihn, solange er der ERSTE ist; beim Abbau gibt
  // er ihn an den nächsten Wartenden weiter (R16-W6).
  const [hatKurzbefehl, setHatKurzbefehl] = useState(false);
  useEffect(() => {
    if (!shortcut) return undefined;
    if (kurzbefehlWartende.length > 0 && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `ListToolbar: der Kurzbefehl \`${shortcut}\` ist auf dieser Seite schon von ` +
          'einer anderen Leiste belegt — nur EINE Listensuche trägt ihn (G0.3).',
      );
    }
    kurzbefehlWartende.push(setHatKurzbefehl);
    verteileKurzbefehl();
    return () => {
      const i = kurzbefehlWartende.indexOf(setHatKurzbefehl);
      if (i >= 0) kurzbefehlWartende.splice(i, 1);
      verteileKurzbefehl();
    };
  }, [shortcut]);

  const searchKnoten = search ? (
    <Box
      sx={{
        flex: 1,
        // 🔴 R15/Fotobefund (Nachbesserung 3): in der KOMPAKTEN Z1 am Tablet
        // bekommt die Suche einen Boden. Ohne ihn drückten „Filters", ⟳ und ⋮
        // sie bei 768 px auf ~0 px (das Foto zeigte nur noch den Rand links
        // neben „Vendor"). Am Telefon bleibt es bei `0` — dort ist die Pille
        // absichtlich schmal und klappt beim Fokus auf.
        // 🔴 Der Wert kommt aus `SUCHE_MIN_BREITE_KOMPAKT` (R15 W4): dieselbe
        // Zahl bindet die Playwright-Probe PW6c, Changelog und Feature-Doc —
        // sie stand vorher an vier Stellen als Literal.
        minWidth: kompakt && !schmal ? SUCHE_MIN_BREITE_KOMPAKT : 0,
        // 🔴 G0b.4 — der gemeldete Deckel (NW4.2:648). Auf einer `full`-Seite
        // fuellt die Suche nur bis `SEARCH_MAX`; der Rest von Z1 bleibt leer, und
        // Zaehler/Seitenaktionen bleiben am rechten Rand (`ml: 'auto'`). Ohne
        // Meldung fehlt der Schluessel ganz — der Kasten waechst wie bisher.
        ...(searchMax == null ? {} : { maxWidth: searchMax }),
        display: 'flex',
        alignItems: 'center',
        ...FIELD_HEIGHT_SX,
        // 🔴 Nur am Desktop: dort zeichnet `SearchTrigger` ein `TextField`, dessen
        // Wurzel von selbst 37,125 px hoch ist (`tokens.js` `FIELD_HEIGHT_SX`
        // rechnet es vor). Mobil ist die Pille schon `FIELD_HEIGHT_SX`; die Zeile
        // hier träfe dort den INNEREN `InputBase` und zöge ihn über die Pille.
        ...(schmal ? {} : { '& .MuiInputBase-root': { ...FIELD_HEIGHT_SX, alignItems: 'center' } }),
      }}
    >
      <SearchTrigger
        {...search}
        focusRef={suchFeldRef}
        shortcut={hatKurzbefehl ? shortcut : undefined}
      />
    </Box>
  ) : null;

  const facettenKnoten = !z1Kompakt && facetten.length > 0 ? (
    <Box
      // 🔴 G14a-2 (2026-10-31): der Facetten-Slot traegt eine MESS-Marke. P23
      // („fremdes gehoert nicht in die Leiste") zaehlt Eingabefelder in Z1
      // ausser der Suche; eine FACETTE ist aber ein Filter und darf ein
      // Bedienelement sein (E4: der Schalter `Unresolved only`). Ohne die Marke
      // meldete die Probe den Schalter als „fremdes" — ein Befund ueber die
      // Messung, nicht ueber die Liste. Sie ist additiv: kein Verhalten, kein
      // Stil, nur ein Attribut (Aufrufer bleiben zeichengleich).
      data-testid="liste-facetten"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: SP.tight,
        flexShrink: 0,
        ...FIELD_HEIGHT_SX,
      }}
    >
      {facetten}
    </Box>
  ) : null;

  const filterKnoten = moreFilters ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        ...FIELD_HEIGHT_SX,
        '& .MuiButton-root': { ...FIELD_HEIGHT_SX },
      }}
    >
      <FilterSheet variant="popover" countInLabel {...moreFilters} />
    </Box>
  ) : null;

  // 🔴 S3 (2026-11-01): der opt-in Zusatz-Slot. Er steht VOR der rechten Gruppe
  // (Zähler · Ansicht · ⟳ · ⋮) und auf jeder Breite — der Grund steht am
  // `@props`-Eintrag `z1Extra`. Der Kasten trägt `FIELD_HEIGHT_SX`, damit W.2
  // (gleiche Höhe aller direkten Z1-Kinder) auch mit ihm gilt.
  const z1ExtraKnoten = z1Extra ? (
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ...FIELD_HEIGHT_SX }}>
      {z1Extra}
    </Box>
  ) : null;

  // Die rechte Gruppe: Zaehler (nur Desktop), Ansicht/Sortierung, Auffrischen,
  // Seitenaktionen. EIN Kind von Z1, damit die Hoehenprobe (W.2) ueber die
  // direkten Kinder laeuft.
  // 🔴 Reihenfolge nach §2.1 5a: … · Treffer-Zähler · Ansicht/Sortierung ·
  // `RefreshButton` · ⋮ Seitenaktionen. `view` sitzt deshalb ZWISCHEN Zähler und
  // Auffrischen.
  const rechtsKnoten = (!z1Kompakt && count) || view || refresh || hatAktionen ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: SP.tight,
        flexShrink: 0,
        ml: 'auto',
        ...FIELD_HEIGHT_SX,
      }}
    >
      {!z1Kompakt && count && <ResultCount {...count} />}
      {view}
      {refresh}
      {hatAktionen && <RowActionsMenu actions={actions} label={actionsLabel} />}
    </Box>
  ) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', ...sx }}>
      {/* Z1 — Such-Pille · Facetten (≤ 3, Desktop) · „Filters" · Zähler · ⟳ · ⋮ */}
      <Box
        data-testid="liste-z1"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: SP.tight,
          minWidth: 0,
          // 🔴 Nur in der kompakten Tablet-Form: `kompakt && !schmal` schaltet
          // den Umbruch für **600–899 px** frei (unter 600 ist die Form ohnehin
          // mobil, ab 900 greift die Karte). Gebraucht wird er unterhalb
          // ~768 px; darüber bleibt er freigeschaltet und ungenutzt. Die
          // frühere Angabe „600–767" beschrieb den BEDARF, nicht die Bedingung
          // (R15 W6).
          flexWrap: kompakt && !schmal ? 'wrap' : 'nowrap',
        }}
      >
        {searchKnoten}
        {facettenKnoten}
        {filterKnoten}
        {z1ExtraKnoten}
        {rechtsKnoten}
      </Box>

      {/* Z2 — Quick-Filter-Pillen (§2.1, Höhe `CONTROL_HEIGHT_SX` über `GLASS_PILL_SX`) */}
      {quickFilters && (
        <Box data-testid="liste-z2" sx={{ mt: SP.tight, minWidth: 0 }}>
          {quickFilters}
        </Box>
      )}

      {/* Z3 — die Zeile der aktiven Filter (§4.1). `ActiveFilterBar` traegt die
          Marke `liste-z3` selbst und rendert nichts, wenn nichts einengt (mobil
          ausser fuer den Zaehler, N11.10). */}
      <ActiveFilterBar
        chips={query?.chips}
        onClearAll={query?.clearAll}
        count={count}
        // 🔴 R15/Fotobefund: der Zähler ist aus Z1 gewandert — Z3 muss ihn
        // deshalb auch dann tragen, wenn die Fensterbreite ihn nicht dorthin
        // schickt (`kompakt` am Tablet).
        kompakt={z1Kompakt}
        // 🔴 G11c-Nachbesserung 2 (2026-10-31): der Umbruch der Z3-Zeile auf
        // dem Telefon ist ein Opt-in des Aufrufers. Vorgabe `false` haelt alle
        // uebrigen Listen zeichengleich (P2/M1 der Kette `g11cc`).
        mobilUmbruch={z3UmbruchMobil}
        returnFocusRef={suchFeldRef}
        sx={{ mt: SP.tight }}
      />

      {children}
    </Box>
  );
}
